/**
 * AdminReports — Phase 1 data exports (Bookings / Payments / Customers).
 *
 * Each card downloads a CSV by hitting `/api/admin/exports/<dataset>.csv`.
 * The Bookings + Payments cards accept an optional date range and (for
 * bookings) a status multi-select. Customers exports the full registered
 * list with per-customer booking counts — no filters needed.
 *
 * PII note: these CSVs include decrypted phone numbers (and email for
 * customers). Admin auth is already 2FA-gated; the page surfaces this
 * caveat visibly so operators know what they're about to share.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, ShieldAlert, Users, CreditCard, ClipboardList, Archive } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["PENDING", "PENDING_REVIEW", "CONFIRMED", "IN_PROGRESS", "COMPLETED", "PAID", "CANCELLED", "DECLINED"];

function triggerCsvDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function parseFilenameFromHeader(headerValue, fallback) {
  const match = (headerValue || "").match(/filename="?([^";]+)"?/i);
  return match ? match[1].trim() : fallback;
}

function ExportCard({ icon: Icon, title, description, onDownload, busy, children }) {
  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-5" data-testid={`export-card-${title.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-sarathi-primary/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-sarathi-primary" />
        </div>
        <div>
          <h2 className="text-white font-semibold text-sm">{title}</h2>
          <p className="text-sarathi-text-muted text-[11px]">{description}</p>
        </div>
      </div>
      {children}
      <div className="mt-4">
        <Button
          onClick={onDownload}
          disabled={busy}
          className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-9 text-sm w-full sm:w-auto"
          data-testid={`export-${title.toLowerCase()}-download-btn`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download CSV</>}
        </Button>
      </div>
    </div>
  );
}

function DateRangeFields({ from, to, setFrom, setTo, idPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">From</label>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm"
          data-testid={`${idPrefix}-from`}
        />
      </div>
      <div>
        <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">To</label>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm"
          data-testid={`${idPrefix}-to`}
        />
      </div>
    </div>
  );
}

export default function AdminReports() {
  // Bookings card state
  const [bFrom, setBFrom] = useState("");
  const [bTo, setBTo] = useState("");
  const [bStatuses, setBStatuses] = useState([]);  // empty = all
  const [bBusy, setBBusy] = useState(false);

  // Payments card state
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const [pBusy, setPBusy] = useState(false);

  // Customers card state
  const [cBusy, setCBusy] = useState(false);

  // Legacy renumbered invoices bundle (Aug 2026, one-shot)
  const [legacyBusy, setLegacyBusy] = useState(false);

  const downloadCsv = async (url, fallbackName, setBusy) => {
    setBusy(true);
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const filename = parseFilenameFromHeader(res.headers["content-disposition"], fallbackName);
      triggerCsvDownload(new Blob([res.data], { type: res.headers["content-type"] || "text/csv;charset=utf-8" }), filename);
      toast.success("Export downloaded");
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Couldn't download export.");
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadBookings = () => {
    const params = new URLSearchParams();
    if (bFrom) params.set("from_date", bFrom);
    if (bTo) params.set("to_date", bTo);
    if (bStatuses.length) params.set("status", bStatuses.join(","));
    const qs = params.toString();
    downloadCsv(`${API}/admin/exports/bookings.csv${qs ? `?${qs}` : ""}`,
                "sarathix-bookings.csv", setBBusy);
  };

  const downloadPayments = () => {
    const params = new URLSearchParams();
    if (pFrom) params.set("from_date", pFrom);
    if (pTo) params.set("to_date", pTo);
    const qs = params.toString();
    downloadCsv(`${API}/admin/exports/payments.csv${qs ? `?${qs}` : ""}`,
                "sarathix-payments.csv", setPBusy);
  };

  const downloadCustomers = () => {
    downloadCsv(`${API}/admin/exports/customers.csv`,
                "sarathix-customers.csv", setCBusy);
  };

  const downloadLegacyBundle = () => {
    downloadCsv(`${API}/admin/legacy-invoices-bundle.zip`,
                "sarathix_renumbered_invoices.zip", setLegacyBusy);
  };

  const toggleStatus = (s) => {
    setBStatuses((prev) => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-4xl" data-testid="admin-reports-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FileSpreadsheet className="w-5 h-5 text-sarathi-primary" />
          <h1 className="text-white text-xl sm:text-2xl font-bold">Reports & Exports</h1>
        </div>
        <p className="text-sarathi-text-muted text-sm">
          Download CSV exports for accounting, GST returns, reconciliation, and customer segmentation.
        </p>
      </div>

      {/* PII caveat — visible on every visit so operators are reminded */}
      <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3" data-testid="reports-pii-caveat">
        <ShieldAlert className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-300 text-xs font-semibold mb-0.5">Contains personal data</p>
          <p className="text-yellow-200/70 text-xs leading-relaxed">
            These exports include passenger phone numbers and emails (decrypted server-side). Handle the CSV files as confidential — never email them to untrusted recipients or upload to public services.
          </p>
        </div>
      </div>

      {/* Bookings */}
      <ExportCard
        icon={ClipboardList}
        title="Bookings"
        description="Every booking with fare breakdown, PII, and timestamps"
        onDownload={downloadBookings}
        busy={bBusy}
      >
        <DateRangeFields from={bFrom} to={bTo} setFrom={setBFrom} setTo={setBTo} idPrefix="bookings-date" />
        <div className="mt-3">
          <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">
            Status filter (optional · click to toggle)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map((s) => {
              const active = bStatuses.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    active
                      ? "bg-sarathi-primary/20 border border-sarathi-primary/40 text-sarathi-primary"
                      : "bg-sarathi-background border border-white/5 text-sarathi-text-muted hover:text-white"
                  }`}
                  data-testid={`bookings-status-toggle-${s}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          {bStatuses.length === 0 && (
            <p className="text-sarathi-text-muted text-[11px] mt-1.5">No filter selected — exporting all statuses.</p>
          )}
        </div>
      </ExportCard>

      {/* Payments */}
      <ExportCard
        icon={CreditCard}
        title="Payments"
        description="All paid bookings (Razorpay + offline) with amounts and methods"
        onDownload={downloadPayments}
        busy={pBusy}
      >
        <DateRangeFields from={pFrom} to={pTo} setFrom={setPFrom} setTo={setPTo} idPrefix="payments-date" />
        <p className="text-sarathi-text-muted text-[11px] mt-2">Date filter applies to <span className="text-white">payment date</span>, not booking date.</p>
      </ExportCard>

      {/* Customers */}
      <ExportCard
        icon={Users}
        title="Customers"
        description="Registered customer accounts with booking counts and last activity"
        onDownload={downloadCustomers}
        busy={cBusy}
      >
        <p className="text-sarathi-text-muted text-[11px]">No filters — exports the full customer list with decrypted phone, email, and lifetime booking count.</p>
      </ExportCard>

      {/* Legacy renumbered invoices — one-shot utility (Aug 2026) */}
      <ExportCard
        icon={Archive}
        title="Legacy Renumbered Invoices"
        description="ZIP of the 10 pre-DB invoices renumbered SX/2026-27/0001..0010"
        onDownload={downloadLegacyBundle}
        busy={legacyBusy}
      >
        <p className="text-sarathi-text-muted text-[11px]">
          One-shot bundle. Contains PDFs for Sunil Sawhney, Chandana Bawa, Vandana Boolel (×3), Amit, Lalji Chhipa, STEPZERO Labs (×2), and Archana Bhaskar with corrected FY-scoped invoice numbers.
        </p>
      </ExportCard>
    </div>
  );
}
